import { findQuoteRoot } from "../composeSendBody";
import { absatzVon, istTabHuelle, TAB_BREITE_ATTR, TAB_KLASSE } from "./tabulator";
import {
    type Abschnitt,
    type Tabstopp,
    MIN_TAB_PT,
    liesTabstopps,
    schreibeTabstopps,
    styleMitTabStopps,
    tabStoppsAusStyle,
    tabZiel,
} from "./tabstopps";
import { istDom } from "../../windowManager/browserFenster/domArt";

/**
 * tabAusrichtung.ts — setzt jeden Tab im Editor auf seinen Tabstopp.
 *
 * ══ WIE EIN TAB AUF EINEN FREIEN STOPP KOMMT ════════════════════════════════
 * CSS kennt keine frei gesetzten Tabstopps, nur `tab-size`: Ein Tab springt
 * auf das nächste Vielfache davon, gemessen vom Zeilenanfang des Absatzes.
 * Bekommt jede Tab-Hülle ihr EIGENES `tab-size` — genau den Abstand vom
 * Zeilenanfang bis zum Ziel —, landet der Text dahinter exakt dort. In
 * Chromium/Edge nachgemessen.
 *
 * Deshalb steht in jeder Hülle genau EIN Tab: Zwei Tabs hintereinander legt
 * der Browser in dieselbe Hülle (`\t\t`), und die teilten sich ein `tab-size`.
 * Eingesetztes HTML wird VOR dem Einsetzen geteilt (`tabsVorbereiten`), damit
 * Strg+Z sauber bleibt; `tabHuellenTeilen` ist nur der Rückfall.
 *
 * Gemessen wird vom linken Rand der Schreibfläche — wie das Lineal und wie
 * Word, das Tabstopps vom Seitenrand aus zählt, nicht vom Einzug. Wo ein Tab
 * beginnt, sagt das Ende des Inhalts DAVOR, nicht die Hülle selbst: Die
 * rutscht mit ihrem eigenen `tab-size` womöglich in die nächste Zeile, und
 * wer an ihr mäße, rechnete im nächsten Lauf etwas anderes aus — der Tab
 * spränge endlos zwischen zwei Zeilen hin und her.
 *
 * Der zitierte Verlauf bleibt unberührt: Er geht so hinaus, wie er kam.
 * Erkannt wird er wie beim Senden (`findQuoteRoot`), also auch dann, wenn der
 * Marker beim Speichern eines Entwurfs weggefallen ist.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const TABSTOPP_ATTR = "data-tabstopps";

/**
 * Vorfilter für Tab-Hüllen: Ein zitierter Outlook-Verlauf bringt leicht
 * Tausende `<span>` mit, und die Ausrichtung läuft nach jedem Tastendruck.
 * Jede Hülle trägt `white-space:pre` im Style; die genaue Prüfung macht
 * danach `istTabHuelle`.
 */
const HUELLEN = 'span[style*="pre"]';
const PX_ZU_PT = 0.75;
/** Blöcke — davor beginnt eine neue Zeile, ein Tab dahinter am Zeilenanfang. */
const BLOCK_TAGS = new Set(["DIV", "P", "UL", "OL", "LI", "TABLE", "BLOCKQUOTE", "PRE", "HR", "H1", "H2", "H3", "H4", "H5", "H6"]);

const istBlock = (n: Node | null): boolean => n?.nodeType === 1 && BLOCK_TAGS.has((n as Element).tagName);

const imZitat = (zitat: Element | null, n: Node): boolean => !!zitat && zitat.contains(n);

/** Steht `n` im zitierten Verlauf? Dort gibt es keine Tabstopps zu setzen. */
export function imZitierten(root: HTMLElement, n: Node): boolean {
    return imZitat(findQuoteRoot(root), n);
}

/** Linke bzw. rechte Kante des Inhalts (innerhalb von Rahmen und Innenabstand) in px. */
function inhaltLinks(el: HTMLElement): number {
    const cs = el.ownerDocument.defaultView?.getComputedStyle(el);
    return el.getBoundingClientRect().left + el.clientLeft + (parseFloat(cs?.paddingLeft ?? "0") || 0);
}
function inhaltRechts(el: HTMLElement): number {
    const cs = el.ownerDocument.defaultView?.getComputedStyle(el);
    return el.getBoundingClientRect().left + el.clientLeft + el.clientWidth - (parseFloat(cs?.paddingRight ?? "0") || 0);
}

/**
 * Ein Stopp-Wert, wie er in den Style darf. Der rohe Wert aus Outlook bleibt
 * stehen (mit Füllpunkten, Leiste- und Listen-Tabs, die das Lineal nicht
 * kennt); alles, was kein Word-Format ist, wird neu geschrieben — sonst
 * schmuggelte ein eingefügtes `data-tabstopps` beliebiges CSS in den Style.
 */
function sichererWert(wert: string): string {
    const w = wert.trim();
    return /^[a-z0-9.\s-]*$/i.test(w) ? w : schreibeTabstopps(liesTabstopps(w));
}

/**
 * Übernimmt `tab-stops` aus geladenem HTML in `data-tabstopps` — unverändert,
 * damit es beim Speichern genauso wieder hinausgeht. Was schon ein Attribut
 * hat, bleibt: Das Attribut ist der Stand im Editor.
 */
function stoppsInsAttribut(inhalt: ParentNode, zitat: Element | null): void {
    inhalt.querySelectorAll<HTMLElement>('[style*="tab-stops"]').forEach((el) => {
        if (el.hasAttribute(TABSTOPP_ATTR) || imZitat(zitat, el)) return;
        const wert = tabStoppsAusStyle(el.getAttribute("style"));
        if (wert != null) el.setAttribute(TABSTOPP_ATTR, sichererWert(wert));
    });
}

/**
 * Trennt Hüllen mit mehreren Tabs in je eine Hülle pro Tab. Die neuen Hüllen
 * kommen VOR die alte, und die alte verliert ihre ersten Zeichen per
 * `deleteData` — so bleibt die Einfügemarke, wo sie war (die Auswahl ist ein
 * lebender Range und rückt mit).
 */
function huellenTeilen(inhalt: ParentNode, zitat: Element | null): void {
    inhalt.querySelectorAll<HTMLElement>(HUELLEN).forEach((span) => {
        if (!istTabHuelle(span) || imZitat(zitat, span)) return;
        const text = span.firstChild;
        if (!text || text.nodeType !== 3 || span.childNodes.length !== 1) return;
        const t = text as Text;
        while (t.length > 1) {
            const neu = span.cloneNode(false) as HTMLElement;
            neu.textContent = "\t";
            span.parentNode?.insertBefore(neu, span);
            t.deleteData(0, 1);
        }
    });
}

/** Rückfall im Editor, falls doch eine Hülle mit mehreren Tabs entsteht. */
export function tabHuellenTeilen(root: HTMLElement): void {
    huellenTeilen(root, findQuoteRoot(root));
}

/** `tab-stops` aus geladenem HTML ins Attribut (siehe `stoppsInsAttribut`). */
export function tabstoppsEinlesen(root: HTMLElement): void {
    stoppsInsAttribut(root, findQuoteRoot(root));
}

/**
 * Bereitet HTML vor, bevor es in den Editor kommt (Entwurf, Vorlage,
 * Signatur, Einfügen). Beim Einsetzen per `insertHTML` verwirft Chromium
 * `tab-stops` sofort, und Outlook/Word legen einen Tab als
 * `<span style="mso-tab-count:1">` voller Leerzeichen ab, aus dem Leerzeichen
 * würden. Deshalb vorher, im Text:
 *  - `tab-stops` → `data-tabstopps` (übersteht `insertHTML`),
 *  - Word-Tabs → je Tab eine echte Tab-Hülle,
 *  - Hüllen mit mehreren Tabs → eine je Tab (nachträglich geteilt, brächte
 *    das Strg+Z durcheinander).
 * Das Zitat bleibt, wie es ist.
 */
export function tabsVorbereiten(html: string): string {
    if (!new RegExp(`\\t|tab-stops|mso-tab-count|${TAB_KLASSE}`, "i").test(html)) return html;
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const inhalt = tpl.content;
    const zitat = findQuoteRoot(inhalt);
    stoppsInsAttribut(inhalt, zitat);
    // Auch der versendete Tab-Kasten (`tabsFuerVersand`) wird wieder ein Tab —
    // ein gespeicherter Entwurf kommt so zurück, wie er geschrieben wurde.
    // Graph liefert ihn ohne `mso-tab-count`; die Klasse bleibt.
    inhalt.querySelectorAll<HTMLElement>(`span[style*="mso-tab-count"], span.${TAB_KLASSE}`).forEach((span) => {
        if (imZitat(zitat, span)) return;
        const m = /mso-tab-count\s*:\s*(\d+)/i.exec(span.getAttribute("style") ?? "");
        const anzahl = Math.min(20, Math.max(1, m ? parseInt(m[1], 10) : 1));
        const doc = span.ownerDocument;
        const frag = doc.createDocumentFragment();
        for (let i = 0; i < anzahl; i++) {
            const huelle = doc.createElement("span");
            huelle.setAttribute("style", "white-space:pre");
            huelle.textContent = "\t";
            frag.appendChild(huelle);
        }
        span.replaceWith(frag);
    });
    huellenTeilen(inhalt, zitat);
    return tpl.innerHTML;
}

/** Folgeknoten in Dokumentreihenfolge, ohne in `n` hineinzugehen. */
function nachTeilbaum(n: Node, grenze: Node): Node | null {
    let k: Node | null = n;
    while (k && k !== grenze) {
        if (k.nextSibling) return k.nextSibling;
        k = k.parentNode;
    }
    return null;
}

function naechsterImBaum(n: Node, grenze: Node): Node | null {
    return n.firstChild ?? nachTeilbaum(n, grenze);
}

/** Breite eines Textstücks in px — Summe seiner Zeilenstücke, also unabhängig vom Umbruch. */
function textBreite(t: Text, bis = t.length): number {
    if (bis <= 0) return 0;
    const r = t.ownerDocument.createRange();
    r.setStart(t, 0);
    r.setEnd(t, bis);
    let summe = 0;
    for (const rechteck of Array.from(r.getClientRects())) summe += rechteck.width;
    return summe;
}

/**
 * Misst den Text hinter einer Tab-Hülle bis zum nächsten Tab, Zeilenumbruch
 * oder Absatzende — für Rechts-, Zentriert- und Komma-Stopps.
 *
 * Gezählt wird Textstück für Textstück. Das umschließende Rechteck taugt
 * nicht: Bricht der Text gerade um, reicht es über die ganze Zeile, und der
 * nächste Lauf rechnete mit einem viel zu breiten Abschnitt.
 */
export function messeAbschnitt(span: HTMLElement, absatz: HTMLElement): Abschnitt {
    let breite = 0;
    let bisKomma: number | null = null;
    for (let n = nachTeilbaum(span, absatz); n && absatz.contains(n); n = naechsterImBaum(n, absatz)) {
        if (n.nodeType === 1) {
            const el = n as HTMLElement;
            if (istTabHuelle(el) || el.tagName === "BR" || istBlock(el)) break;
            if (el.tagName === "IMG") breite += el.getBoundingClientRect().width;
            continue;
        }
        if (n.nodeType !== 3) continue;
        const t = n as Text;
        const komma: number = bisKomma == null ? t.data.indexOf(",") : -1;
        if (komma >= 0) bisKomma = (breite + textBreite(t, komma)) * PX_ZU_PT;
        breite += textBreite(t);
    }
    return { breite: breite * PX_ZU_PT, bisKomma };
}

/** Rechte Kante des letzten sichtbaren Inhalts in `n` (px); `"zeile"`: davor beginnt eine neue Zeile. */
function endeVon(n: Node): number | "zeile" | null {
    if (n.nodeType === 3) {
        const t = n as Text;
        if (!t.length) return null;
        const r = t.ownerDocument.createRange();
        r.setStart(t, t.length - 1);
        r.setEnd(t, t.length);
        const rechtecke = r.getClientRects();
        const letztes = rechtecke[rechtecke.length - 1];
        return letztes && (letztes.width > 0 || letztes.height > 0) ? letztes.right : null;
    }
    if (n.nodeType !== 1) return null;
    const el = n as HTMLElement;
    if (el.tagName === "BR" || istBlock(el)) return "zeile";
    if (el.tagName === "IMG" || istTabHuelle(el)) {
        const rechtecke = el.getClientRects();
        const letztes = rechtecke[rechtecke.length - 1];
        return letztes ? letztes.right : null;
    }
    for (let k = el.lastChild; k; k = k.previousSibling) {
        const e = endeVon(k);
        if (e != null) return e;
    }
    return null;
}

/** Wo der Inhalt vor der Hülle endet (px); `null`: Die Hülle steht am Zeilenanfang. */
function endeDavor(span: HTMLElement, block: HTMLElement): number | null {
    for (let n: Node | null = span; n && n !== block; n = n.parentNode) {
        for (let s = n.previousSibling; s; s = s.previousSibling) {
            const e = endeVon(s);
            if (e === "zeile") return null;
            if (e != null) return e;
        }
    }
    return null;
}

/** Wie weit der Text eines Absatzes vom Rand der Schreibfläche einrückt, in pt (für das Lineal). */
export function einzugImEditor(root: HTMLElement, absatz: HTMLElement): number {
    return Math.max(0, (inhaltLinks(absatz) - inhaltLinks(root)) * PX_ZU_PT);
}

/** Die Stopps eines Absatzes (leer: nur die Standard-Stopps). */
export function stoppsVon(absatz: HTMLElement | "root" | null): Tabstopp[] {
    return istDom(absatz, "HTMLElement") ? liesTabstopps(absatz.getAttribute(TABSTOPP_ATTR)) : [];
}

/**
 * Richtet alle Tabs außerhalb des Zitats aus. Schreibt nur, wo sich der Wert
 * ändert. Ohne Layout (verborgen, jsdom) passiert gar nichts.
 */
export function tabsAusrichten(root: HTMLElement): void {
    if (!root.isConnected || root.getBoundingClientRect().width === 0) return;
    const zitat = findQuoteRoot(root);
    stoppsInsAttribut(root, zitat);
    huellenTeilen(root, zitat);
    const rand = inhaltLinks(root);
    const breite = (inhaltRechts(root) - rand) * PX_ZU_PT;
    root.querySelectorAll<HTMLElement>(HUELLEN).forEach((span) => {
        if (!istTabHuelle(span) || imZitat(zitat, span)) return;
        const absatz = absatzVon(root, span);
        if (absatz === null) return;
        const block = absatz === "root" ? root : absatz;
        const zeilenanfang = inhaltLinks(block) - rand;
        const x = ((endeDavor(span, block) ?? inhaltLinks(block)) - rand) * PX_ZU_PT;
        // Nie über den rechten Rand hinaus — ein Stopp jenseits davon (Vorlage
        // aus einem breiteren Fenster) zöge den Tab sonst in die nächste Zeile.
        const ziel = Math.min(tabZiel(x, stoppsVon(absatz), messeAbschnitt(span, block)), Math.max(breite, x + MIN_TAB_PT));
        // `tab-size` zählt vom Zeilenanfang DIESES Absatzes (Einzug!), das Ziel vom Rand.
        const groesse = ziel / PX_ZU_PT - zeilenanfang;
        // Rundungszittern (hundertstel Pixel) ist keine Änderung.
        const bisher = parseFloat(span.style.tabSize);
        if (!(Math.abs(bisher - groesse) < 0.05)) span.style.tabSize = `${groesse.toFixed(2)}px`;
    });
}

/**
 * Das HTML für Speichern und Senden: `data-tabstopps` wird wieder zu
 * `tab-stops` im Style — dem Format, das Outlook Desktop versteht. Ein Wert,
 * den niemand geändert hat, bleibt Zeichen für Zeichen, wie er kam. Ohne
 * Stopps im Editor bleibt das ganze HTML unberührt.
 */
export function htmlFuerAusgabe(root: HTMLElement): string {
    if (!root.querySelector(`[${TABSTOPP_ATTR}]`)) return root.innerHTML;
    const kopie = root.cloneNode(true) as HTMLElement;
    stoppsAlsStyle(kopie);
    return kopie.innerHTML;
}

function stoppsAlsStyle(kopie: HTMLElement): void {
    kopie.querySelectorAll<HTMLElement>(`[${TABSTOPP_ATTR}]`).forEach((el) => {
        const wert = sichererWert(el.getAttribute(TABSTOPP_ATTR) ?? "");
        el.removeAttribute(TABSTOPP_ATTR);
        if (tabStoppsAusStyle(el.getAttribute("style")) === wert) return;
        const style = styleMitTabStopps(el.getAttribute("style"), wert || null);
        if (style) el.setAttribute("style", style);
        else el.removeAttribute("style");
    });
}

/**
 * Das HTML zum Senden (und für den Entwurf): wie {@link htmlFuerAusgabe},
 * dazu trägt jede Tab-Hülle die Breite, die sie im Editor gerade einnimmt
 * (`data-tab-breite`, px). Daraus baut `tabsFuerVersand` einen Kasten fester
 * Breite — das Tab-Zeichen selbst macht Exchange beim Senden zum Leerzeichen.
 * Nur hier, nicht in `getHtml`: Signaturen und Vorlagen werden beim Einsetzen
 * neu ausgerichtet, ein mitgespeicherter Messwert wäre dort falsch.
 */
export function htmlFuerVersand(root: HTMLElement): string {
    const kopie = root.cloneNode(true) as HTMLElement;
    stoppsAlsStyle(kopie);
    const zitat = findQuoteRoot(root);
    const echt = Array.from(root.querySelectorAll<HTMLElement>(HUELLEN));
    const kopien = Array.from(kopie.querySelectorAll<HTMLElement>(HUELLEN));
    echt.forEach((span, i) => {
        if (!istTabHuelle(span) || imZitat(zitat, span)) return;
        let breite = 0;
        for (const r of Array.from(span.getClientRects())) breite += r.width;
        if (breite > 0) kopien[i]?.setAttribute(TAB_BREITE_ATTR, breite.toFixed(2));
    });
    return kopie.innerHTML;
}

/**
 * HTML ohne das, was das Ausrichten daran ändert — zum Vergleichen, ob jemand
 * etwas bearbeitet hat (die Signatur prüft so, ob sie noch getauscht werden
 * darf). Gestrichen werden `tab-size`, die Aufteilung in eine Hülle je Tab
 * und die Schreibweise der Stopps; ein geänderter Stopp bleibt ein
 * Unterschied.
 */
export function htmlOhneAusrichtung(html: string): string {
    if (!html.includes("\t") && !html.includes("tab-stops") && !html.includes(TABSTOPP_ATTR)) return html;
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const inhalt = tpl.content;
    inhalt.querySelectorAll<HTMLElement>(`[${TABSTOPP_ATTR}], [style*="tab-stops"]`).forEach((el) => {
        const roh = el.hasAttribute(TABSTOPP_ATTR) ? el.getAttribute(TABSTOPP_ATTR) : tabStoppsAusStyle(el.getAttribute("style"));
        const wert = schreibeTabstopps(liesTabstopps(roh));
        el.removeAttribute(TABSTOPP_ATTR);
        const style = styleMitTabStopps(el.getAttribute("style"), wert || null);
        if (style) el.setAttribute("style", style);
        else el.removeAttribute("style");
    });
    inhalt.querySelectorAll<HTMLElement>(HUELLEN).forEach((span) => {
        if (!istTabHuelle(span)) return;
        span.style.removeProperty("tab-size");
        // Einheitlich aus dem CSSOM schreiben — sonst unterschiede sich
        // „white-space:pre" von „white-space: pre;" nur in der Schreibweise.
        span.setAttribute("style", span.style.cssText);
        const davor = span.previousSibling;
        if (istTabHuelle(davor) && (davor as HTMLElement).getAttribute("style") === span.getAttribute("style")) {
            davor!.textContent = (davor!.textContent ?? "") + (span.textContent ?? "");
            span.remove();
        }
    });
    return tpl.innerHTML;
}

/**
 * Fasst die losen Zeilen, in denen `knoten` stehen, je in ein eigenes `<div>`
 * — per DOM, Zeile für Zeile. Nicht per `formatBlock`: Das wirkt auf die
 * ganze Auswahl und schmolz bei Strg+A alle Absätze zu einem zusammen.
 * Eine lose Zeile reicht vom letzten Block bzw. `<br>` bis einschließlich zum
 * nächsten `<br>`. Die Auswahl bleibt stehen.
 */
function loseZeilenFassen(root: HTMLElement, knoten: readonly Node[]): void {
    const doc = root.ownerDocument;
    const sel = doc.getSelection();
    const alt = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const merk = alt && { sc: alt.startContainer, so: alt.startOffset, ec: alt.endContainer, eo: alt.endOffset };

    const anfaenge = new Set<Node>();
    for (const n of knoten) {
        let oben: Node = n;
        while (oben.parentNode && oben.parentNode !== root) oben = oben.parentNode;
        if (oben.parentNode !== root || istBlock(oben)) continue;
        let a = oben;
        while (a.previousSibling && !istBlock(a.previousSibling) && a.previousSibling.nodeName !== "BR") a = a.previousSibling;
        anfaenge.add(a);
    }
    for (const a of anfaenge) {
        if (a.parentNode !== root) continue;
        const div = doc.createElement("div");
        root.insertBefore(div, a);
        for (let n: Node | null = a; n && !istBlock(n); ) {
            const weiter: Node | null = n.nextSibling;
            div.appendChild(n);
            if (n.nodeName === "BR") break;
            n = weiter;
        }
    }

    if (merk && sel) {
        const passt = (c: Node, o: number) => Math.min(o, c.nodeType === 3 ? (c as Text).length : c.childNodes.length);
        const r = doc.createRange();
        r.setStart(merk.sc, passt(merk.sc, merk.so));
        r.setEnd(merk.ec, passt(merk.ec, merk.eo));
        sel.removeAllRanges();
        sel.addRange(r);
    }
}

/**
 * Die Absätze, die die Auswahl berührt (ohne Zitat) — auch leere Zeilen. Steht
 * Text direkt im Editor (die erste Zeile in Chromium), wird diese Zeile auf
 * Wunsch erst in ein `<div>` gefasst: Ein Tabstopp braucht einen Absatz, an
 * dem er hängt. Im leeren Editor entsteht dafür eine leere Zeile.
 */
export function absaetzeDerAuswahl(root: HTMLElement, fassen = false): HTMLElement[] {
    const doc = root.ownerDocument;
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return [];
    const r = sel.getRangeAt(0);
    if (!root.contains(r.commonAncestorContainer)) return [];
    const zitat = findQuoteRoot(root);

    // Ein Randpunkt direkt im Editor (Strg+A, leerer Editor) zeigt auf ein Kind.
    const kindAm = (c: Node, o: number): Node => (c === root ? (root.childNodes[o] ?? root.childNodes[o - 1] ?? root) : c);
    const knoten: Node[] = [kindAm(r.startContainer, r.startOffset)];
    if (!r.collapsed) {
        const walker = doc.createTreeWalker(r.commonAncestorContainer, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
        for (let n = walker.nextNode(); n; n = walker.nextNode()) {
            if ((n.nodeType === 3 || n.nodeName === "BR") && r.intersectsNode(n)) knoten.push(n);
        }
        knoten.push(kindAm(r.endContainer, r.endOffset));
    }

    if (fassen) {
        if (knoten[0] === root && !root.firstChild) {
            // Leerer Editor: eine leere Zeile anlegen und den Cursor hineinsetzen.
            const div = doc.createElement("div");
            div.appendChild(doc.createElement("br"));
            root.appendChild(div);
            const neu = doc.createRange();
            neu.setStart(div, 0);
            neu.collapse(true);
            sel.removeAllRanges();
            sel.addRange(neu);
            return [div];
        }
        const lose = knoten.filter((n) => n !== root && !imZitat(zitat, n) && absatzVon(root, n) === "root");
        if (lose.length) loseZeilenFassen(root, lose);
    }

    const absaetze = new Set<HTMLElement>();
    for (const n of knoten) {
        if (n === root || imZitat(zitat, n)) continue;
        const a = absatzVon(root, n);
        if (istDom(a, "HTMLElement")) absaetze.add(a);
    }
    return [...absaetze];
}

/** Ändert die Stopps der gegebenen Absätze. Leere Liste = keine eigenen Stopps mehr. */
export function stoppsAendern(absaetze: readonly HTMLElement[], aendern: (alt: Tabstopp[]) => Tabstopp[]): void {
    for (const a of absaetze) {
        a.setAttribute(TABSTOPP_ATTR, schreibeTabstopps(aendern(stoppsVon(a))));
    }
}
